import { createSlice } from '@reduxjs/toolkit';
import { isUneligibleMoveInError } from 'components/direct-sales/quote/utils';
import type { NormalizedError } from 'errors/types';
import type { RootState } from 'state/store';

export type MoveInDateRegister = {
  id: string | number;
  period?: string;
  digits?: string;
};

export type VerifyCallResponse = {
  fuelType: string;
  accountId: string;
  supplyStartDate: string;
  fuel: {
    electricity?: {
      meterPointId: string;
      registers: MoveInDateRegister[];
    };
    gas?: {
      meterPointId: string;
      registers: MoveInDateRegister[];
    };
  };
};

export type MoveInDateState = {
  isLoading: boolean;
  errors: NormalizedError[] | null;
  data: {
    moveInDate: string | null;
    supplyStartDate: string | null;
    verifyCallResponse: VerifyCallResponse | null;
  };
};

export const initialState: MoveInDateState = {
  isLoading: false,
  errors: null,
  data: {
    moveInDate: null,
    supplyStartDate: null,
    verifyCallResponse: null
  }
};

export const slice = createSlice({
  name: 'moveInDate',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state.isLoading = initialState.isLoading;
      state.errors = initialState.errors;
      state.data = initialState.data;
    },
    setMoveInDate: (state, { payload }) => {
      state.data.moveInDate = payload;
    },
    setSupplyStartDate: (state, { payload }) => {
      state.data.supplyStartDate = payload;
    },
    setIsLoading: (state, { payload = true }: { payload?: boolean }) => {
      state.isLoading = payload;
    },
    setError: (state, { payload }) => {
      state.isLoading = initialState.isLoading;
      state.errors = payload;
    },
    setSuccess: (state, { payload }) => {
      state.isLoading = initialState.isLoading;
      state.data.verifyCallResponse = payload.data;
    }
  }
});

export const {
  setInitialState,
  setMoveInDate,
  setSupplyStartDate,
  setIsLoading,
  setError,
  setSuccess
} = slice.actions;

export const selectSlice = (state: RootState) => state[slice.name];
export const selectData = (state: RootState) => selectSlice(state).data;
export const selectErrors = (state: RootState) => selectSlice(state).errors;
export const selectErrorsToBeDisplayed = (state: RootState) => {
  const errors = selectErrors(state) || [];
  return errors.filter((error) => error && !isUneligibleMoveInError(error));
};

export const selectIsLoading = (state: RootState) =>
  selectSlice(state).isLoading;
export const selectVerifyApiErrors = (state: RootState) => {
  const errors = selectErrors(state) || [];
  return errors.filter((error) => error && error.status === '500');
};
export const selectMoveInDate = (state: RootState) =>
  selectData(state).moveInDate;
export const selectAccountId = (state: RootState) =>
  selectVerifyCallResponse(state)?.accountId;
export const selectFuelType = (state: RootState) =>
  selectVerifyCallResponse(state)?.fuelType;
export const selectSupplyStartDate = (state: RootState) =>
  selectData(state).supplyStartDate;
export const selectVerifyCallResponse = (state: RootState) =>
  selectData(state).verifyCallResponse;
export const selectElectricityRegisters = (state: RootState) =>
  selectVerifyCallResponse(state)?.fuel.electricity?.registers;
export const selectGasRegisters = (state: RootState) =>
  selectVerifyCallResponse(state)?.fuel.gas?.registers;
export const selectElectricityMeterPointId = (state: RootState) =>
  selectVerifyCallResponse(state)?.fuel.electricity?.meterPointId;
export const selectGasMeterPointId = (state: RootState) =>
  selectVerifyCallResponse(state)?.fuel.gas?.meterPointId;

export default slice.reducer;
